import { useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Scales } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import { absDiffGrams, formatGrams } from '@/lib/weight'
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  KgInput,
  PickerSheet,
  Skeleton,
  useToast,
} from '@/ui'
import { GateScreen, ActionBar } from '../components/GateScreen'
import { RollConfirm, type PendingRoll } from '../components/RollConfirm'
import { RosterCard } from '../components/RosterCard'
import { useSupervisorPin } from '../components/SupervisorPin'
import { useCropLabel } from '../components/labels'
import { openDay } from '../data/actions'
import { isBlocked, useGate } from '../data/useGate'
import { anyUsableMainCard, hasNoDeductions, inForce } from '../model/rateCards'

const NOT_REGISTERED = '__none__'

/** F4.1 — संग्रह दिवस शुरू करें · Open collection day. */
export function OpenDayPage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const navigate = useNavigate()
  const actor = useActor()
  const g = useGate()
  const sup = useSupervisorPin()
  const cropLabel = useCropLabel(g.roster?.config)
  const [now] = useState(() => Date.now())
  const today = istDateKey(now)
  const [scale, setScale] = useState<string | null>(null)
  const [read, setRead] = useState<number | null>(null)
  const [tagRoll, setTagRoll] = useState<{ roll: PendingRoll; actorId: string } | null>(null)
  const [sealRoll, setSealRoll] = useState<{ roll: PendingRoll; actorId: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const used = useMemo(() => [...g.used.bound, ...g.used.voided], [g.used])

  if (!g.ready || !g.roster) return <Skeleton className="h-64 w-full" />
  if (g.app) return <Navigate to="/m/gate" replace />
  const r = g.roster
  const reg = g.info.registration

  const scales = r.equipment.filter((e) => e.category === 'SCALE')
  const picked = scales.find((s) => s.id === scale) ?? null
  const stampOk = picked ? !!picked.stamp_due_at && picked.stamp_due_at.slice(0, 10) >= today : null
  const testG = r.config.test_weight_g
  const tolG = r.config.test_weight_tolerance_g
  const off = read !== null && absDiffGrams(read, testG) > tolG
  const blocked = isBlocked(actor.user_id, r, g.sessions, g.outbox)
  const cardsOk = anyUsableMainCard(r.rateCards, today)

  const problems: string[] = []
  if (blocked) problems.push(t('gate.open.blocked'))
  if (g.info.freshness === 'expired' || g.info.freshness === 'missing')
    problems.push(t('device.rosterExpired'))
  if (!cardsOk) problems.push(t('gate.open.noCard'))
  if (!scale) problems.push(t('gate.open.pickScale'))
  if (read === null) problems.push(t('gate.open.typeTest'))
  else if (off) problems.push(t('gate.open.testOff', { diff: formatGrams(Math.abs(read - testG)) }))
  if (!tagRoll) problems.push(t('gate.open.needRoll'))
  if (!sealRoll) problems.push(t('gate.open.needSeals'))

  const crops = [...new Set(r.rateCards.filter((c) => inForce(c, today)).map((c) => c.crop_id))]

  const submit = async () => {
    if (problems.length || !tagRoll || !sealRoll || read === null || !reg) return
    setBusy(true)
    try {
      await openDay({
        actorId: actor.user_id,
        rosterVersion: g.info.rosterVersion,
        rolls: [tagRoll, sealRoll].map((x) => ({ roll: x.roll, actorId: x.actorId })),
        session: {
          capture_mode: 'APP',
          group_id: reg.team.id,
          village_name: reg.team.name,
          operator_id: actor.user_id,
          operator_name: actor.name,
          opened_at: new Date().toISOString(),
          scale_equipment_id: picked?.id ?? null,
          scale_label: picked ? (picked.name ?? picked.code) : null,
          stamp_ok: stampOk,
          test_weight_g: testG,
          test_weight_read_g: read,
          tags_in_hand: tagRoll.roll.count,
          seals_in_hand: sealRoll.roll.count,
          paper: null,
        },
      })
      toast.success(t('gate.open.opened'))
      navigate('/m/gate', { replace: true })
    } catch (e) {
      toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <GateScreen title="gate.openDay">
      {blocked && (
        <Banner tone="error" title={t('gate.open.blockedTitle')}>
          {t('gate.open.blocked')}
        </Banner>
      )}
      <RosterCard compact />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 text-[14px]">
            <div>
              <div className="text-[12px] text-muted">{t('gate.field.village')}</div>
              <div className="font-semibold">{reg?.team.name ?? '—'}</div>
            </div>
            <div>
              <div className="text-[12px] text-muted">{t('gate.field.date')}</div>
              <div className="font-semibold">{fmt.date(now)}</div>
            </div>
          </div>

          <Field
            label={t('gate.field.scale')}
            required
            hint={picked && !stampOk ? t('gate.open.stampPassed') : undefined}
          >
            <PickerSheet
              title={t('gate.field.scale')}
              value={scale}
              onChange={setScale}
              options={[
                ...scales.map((s) => {
                  const ok = !!s.stamp_due_at && s.stamp_due_at.slice(0, 10) >= today
                  return {
                    value: s.id,
                    label: s.name ?? s.code,
                    description: s.serial_no ?? undefined,
                    meta: (
                      <Badge tone={ok ? 'ok' : 'danger'}>
                        {s.stamp_due_at
                          ? t('gate.open.stampDue', { date: fmt.date(s.stamp_due_at) })
                          : t('gate.open.noStamp')}
                      </Badge>
                    ),
                  }
                }),
                {
                  value: NOT_REGISTERED,
                  label: t('gate.open.notRegistered'),
                  meta: <Badge tone="danger">!</Badge>,
                },
              ]}
            />
          </Field>
          {scale === NOT_REGISTERED && (
            <Banner tone="warn">{t('gate.open.unregisteredWarn')}</Banner>
          )}

          <div className="grid grid-cols-[auto_1fr] items-end gap-3">
            <div className="pb-3">
              <div className="text-[12px] text-muted">{t('gate.field.testWeight')}</div>
              <div className="flex items-center gap-1.5 font-mono text-[18px]">
                <Scales aria-hidden="true" /> {formatGrams(testG)}
              </div>
            </div>
            <Field
              label={t('gate.field.scaleShows')}
              required
              error={off ? t('gate.open.testOffShort') : undefined}
            >
              <KgInput value={read} onChange={setRead} />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader eyebrow={t('gate.roll.tagRoll')} />
        <CardBody>
          <RollConfirm
            kind="BAG"
            operatorId={actor.user_id}
            rolls={r.rolls}
            used={used}
            confirmed={tagRoll}
            onConfirm={(roll, by) => setTagRoll({ roll, actorId: by ?? actor.user_id })}
            onClear={() => setTagRoll(null)}
            requestSupervisor={(why) => sup.request(why)}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader eyebrow={t('gate.roll.sealStrip')} />
        <CardBody>
          <RollConfirm
            kind="SEAL"
            operatorId={actor.user_id}
            rolls={r.rolls}
            used={used}
            confirmed={sealRoll}
            onConfirm={(roll, by) => setSealRoll({ roll, actorId: by ?? actor.user_id })}
            onClear={() => setSealRoll(null)}
            requestSupervisor={(why) => sup.request(why)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader eyebrow={t('gate.field.rateCard')} />
        <CardBody className="flex flex-col gap-2 py-3">
          {crops.length === 0 && <Banner tone="error">{t('gate.open.noCard')}</Banner>}
          {crops.map((crop) => (
            <div
              key={crop}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule/70 pb-2 last:border-b-0"
            >
              <span className="font-semibold">{cropLabel(crop)}</span>
              <span className="flex flex-wrap gap-2 font-mono text-[14px]">
                {(['MAIN', 'CONV'] as const).map((stream) => {
                  const c = r.rateCards.find(
                    (x) => x.crop_id === crop && x.stream === stream && inForce(x, today),
                  )
                  if (!c) return null
                  return hasNoDeductions(c.deduction_rules) && c.price_paise_per_kg !== null ? (
                    <span key={stream}>
                      {t(stream === 'MAIN' ? 'gate.lot.good' : 'gate.lot.red')}{' '}
                      {fmt.money(c.price_paise_per_kg)}/kg
                    </span>
                  ) : (
                    <Badge key={stream} tone="danger">
                      {t('gate.price.hasDeductions')}
                    </Badge>
                  )
                })}
              </span>
            </div>
          ))}
        </CardBody>
      </Card>

      <ActionBar>
        {problems[0] && <p className="text-center text-[13px] text-danger">{problems[0]}</p>}
        <Button
          size="xl"
          fullWidth
          disabled={problems.length > 0}
          loading={busy}
          onClick={() => void submit()}
        >
          {t('gate.open.openDay')}
        </Button>
      </ActionBar>
      {sup.sheet}
    </GateScreen>
  )
}
